import { zodResolver } from "@hookform/resolvers/zod";
import {
  FolderOpen,
  Loader2,
  CheckCircle2,
  XCircle,
  Info,
  Cpu,
  Hand,
  Lock,
  Pencil,
} from "lucide-react";
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { ModuleTypeSelect } from "@/common/components/ModuleTypeSelect";
import PillTabs from "@/common/components/PillTabs";
import TextLabel from "@/common/components/TextLabel";
import Tooltip from "@/common/components/tooltip/Tooltip";
import { WorkspaceType } from "@/common/enums/workspace-type.enum";
import { useAuth } from "@/common/hooks/useAuth";
import { useToast } from "@/common/hooks/useToast";
import {
  AUTOPILOT_SERV,
  MESSAGE_START_RECORDING,
} from "@/common/constants/RecorderActions";
import {
  useSaveTestCase,
  useUpdateTestCase,
  validateURLAccess,
  checkTestCaseNameExists,
} from "@/common/services/scenarios";
import { extractList } from "@/common/utils/apiShape";
import { getReviewerCandidates } from "@/common/utils/reviewerCandidates";
import { Box, Flex, ScrollArea, Text } from "@/compat/radix-themes";
import TextInputControl from "@/components/ui/forms/input/TextInputControl";
import TextAreaControl from "@/components/ui/forms/textarea/TextAreaControl";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { MultiSearchableSelect } from "@/components/ui/multi-searchable-select";
import { MultiUserSelect } from "@/components/ui/multi-user-select";
import { SelectField } from "@/components/ui/select-field";
import { Switch } from "@/components/ui/switch";
import FolderPickerPanel from "@/pages/scenarios/all-scenarios/folder/components/FolderPickerPanel";
import { tabStore } from "@/pages/scenarios/all-scenarios/useOpenTabsStore";
import EnvConfig from "@/config/env";
import { isAutomationCase } from "@/pages/scenarios/all-scenarios/folder/matchesScriptType";

// Standard field limits. Title is capped to the DB column width (VarChar(250));
// description gets a sensible standard cap. Kept intentionally simple — these are
// plain length bounds, no schema migration or extra behaviour.
const TITLE_MAX_LENGTH = 250;
const DESCRIPTION_MAX_LENGTH = 2000;

// Precondition is a plain-text field in this editor, but AI-generated test cases
// may store it as HTML (e.g. "<p class='...'>User is on the login page.</p>").
// Strip tags/entities so the textarea shows clean, editable text.
const stripHtml = (raw?: string | null): string => {
  if (!raw) return "";
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
};

const createSchema = (isMobileNative: boolean) =>
  z.object({
    title: z
      .string()
      .trim()
      .min(1, "Title is required")
      .max(
        TITLE_MAX_LENGTH,
        `Title must be ${TITLE_MAX_LENGTH} characters or fewer`,
      ),
    websiteUrl: isMobileNative
      ? z
          .string()
          .trim()
          .optional()
          .refine(
            (val) => {
              if (!val || val === "" || val === "https://") return true;
              try {
                new URL(val);
                return true;
              } catch {
                return false;
              }
            },
            { message: "Please enter a valid URL (e.g. https://example.com)" },
          )
      : z
          .string()
          .trim()
          .min(1, "Website URL is required")
          .url("Please enter a valid URL (e.g. https://example.com)"),
    description: z
      .string()
      .max(
        DESCRIPTION_MAX_LENGTH,
        `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`,
      )
      .optional(),
  });

// Neat soft-fill input styling (ported from neat-test-space-main).
// Styling is handled by the global Input component (components/ui/input.tsx)

interface CreateTestProps {
  initialTestCaseType?: "automation" | "manual";
  testCaseTypes: any;
  testCasePriorities: any;
  testCaseStatuses: any;
  testData: any;
  requirements: any;
  teamMembers: any;
  testPrerequisite: any;
  testTags: any;
  onChangeTestCaseType: any;
  onCloseDialog: any;
  loading: boolean;
  pathToActiveNode?: string[];
  activeFolderId?: number | null;
  onSavingChange?: (isSaving: boolean) => void;
  onTitleDuplicateChange?: (isDuplicate: boolean) => void;
  /** Notifies the host dialog so it can widen while the folder picker is open */
  onFolderPickerOpenChange?: (open: boolean) => void;
  /** When provided, the form acts as "Edit test case" — pre-fills all fields from existing data */
  editData?: any;
  /** Optional: field names to render read-only. Unused under BR-42 (status never locks); kept as a generic capability. */
  lockedFields?: string[];
  /** Notifies the host dialog whenever the form's dirty state changes (edit mode only). */
  onDirtyChange?: (isDirty: boolean) => void;
}

const CreateTest = forwardRef(
  (
    {
      initialTestCaseType,
      testCaseTypes,
      testCasePriorities,
      testCaseStatuses,
      testData,
      requirements,
      teamMembers,
      testTags,
      testPrerequisite,
      onChangeTestCaseType,
      onCloseDialog,
      pathToActiveNode,
      activeFolderId,
      loading: _loading,
      onSavingChange,
      onTitleDuplicateChange,
      onFolderPickerOpenChange,
      editData,
      lockedFields,
      onDirtyChange,
    }: CreateTestProps,
    ref,
  ) => {
    const isFieldLocked = (field: string) =>
      lockedFields?.includes(field) ?? false;
    const [testCaseType, setTestCaseType] = useState(
      editData ? (isAutomationCase(editData) ? "automation" : "manual") : "automation",
    );
    const [selectedItems, setSelectedItems] = useState<typeof testTags>(
      editData?._prefillTags ?? [],
    );
    const [priority, setPriority] = useState<any>(
      editData?._prefillPriority ?? null,
    );
    const [type, setType] = useState<any>(editData?._prefillType ?? null);
    const [moduleTypeId, setModuleTypeId] = useState<number | string | null>(
      editData?.module_type_id ?? editData?.moduleTypeId ?? null,
    );
    const [moduleTypeName, setModuleTypeName] = useState<string | null>(null);
    const [status, setStatus] = useState<any>(editData?._prefillStatus ?? null);
    const [preRequisite, setPreRequisite] = useState<any>(
      editData?._prefillPreRequisite ?? null,
    );
    const [precondition, setPrecondition] = useState<string>(
      stripHtml(editData?.precondition),
    );
    const [testDataProfile, setTestDataProfile] = useState<any>(
      editData?._prefillTestData ?? null,
    );
    const [startDataset, setStartDataset] = useState(null);
    const [endDataset, setEndDataset] = useState(null);
    const [assignee, setAssignee] = useState<any[]>(
      editData?._prefillAssignee ?? [],
    );
    const [reviewer, setReviewer] = useState<any[]>(
      editData?._prefillReviewer ?? [],
    );
    const [requirement, setRequirement] = useState<any>(
      editData?._prefillRequirement ?? null,
    );
    const [isDataDriven, setIsDataDriven] = useState(
      editData?.isDataDriven === true ||
        editData?.isDataDriven === 1 ||
        editData?.isDataDriven === "1" ||
        String(editData?.isDataDriven ?? editData?.is_data_driven).toLowerCase() ===
          "true",
    );
    const [switchEnabled, setSwitchEnabled] = useState(
      Boolean(editData?._prefillTestData) ||
        Boolean(
          editData?.isDataDriven === true ||
            editData?.isDataDriven === 1 ||
            editData?.isDataDriven === "1" ||
            String(
              editData?.isDataDriven ?? editData?.is_data_driven,
            ).toLowerCase() === "true",
        ),
    );
    const [urlValidationStatus, setUrlValidationStatus] = useState<
      "idle" | "validating" | "accessible" | "not-accessible"
    >("idle");
    const urlValidationTimer = useRef<NodeJS.Timeout | null>(null);
    const [titleCheckStatus, setTitleCheckStatus] = useState<
      "idle" | "checking" | "available" | "duplicate"
    >("idle");
    const titleCheckTimer = useRef<NodeJS.Timeout | null>(null);
    // Monotonic sequence id for the debounced duplicate-name check. Each request
    // captures the current value; a resolution only applies its result if it is
    // still the latest request — preventing a slow, stale response (e.g. for
    // "Logi") from overwriting the status of a newer one (e.g. "Login").
    const titleCheckSeq = useRef(0);
    const { userPreferenceData, useWorkspaceVersions } = useAuth();
    const projectId = userPreferenceData?.data?.projectId;
    const { showToast } = useToast();
    // Safely unwrap option payloads regardless of API shape nesting
    const priorityList = useMemo(
      () => extractList(testCasePriorities),
      [testCasePriorities],
    );
    const typeList = useMemo(() => extractList(testCaseTypes), [testCaseTypes]);
    const statusList = useMemo(
      () => extractList(testCaseStatuses),
      [testCaseStatuses],
    );
    const prerequisiteList = useMemo(
      () => extractList(testPrerequisite),
      [testPrerequisite],
    );
    const testDataList = useMemo(() => extractList(testData), [testData]);
    const requirementList = useMemo(
      () => extractList(requirements),
      [requirements],
    );
    const tagList = useMemo(() => extractList(testTags), [testTags]);
    const teamMemberList = useMemo(
      () => extractList(teamMembers),
      [teamMembers],
    );

    // Reviewer picker: only Tester / Test Lead project members are eligible,
    // and a user can never assign themselves as their own reviewer.
    const currentUserId = userPreferenceData?.data?.userId as
      | number
      | undefined;
    const reviewerCandidates = useMemo(
      () => getReviewerCandidates(teamMemberList, currentUserId),
      [teamMemberList, currentUserId],
    );
    const saveTestCaseMutation = useSaveTestCase();
    const updateTestCaseMutation = useUpdateTestCase(editData?.id ?? 0);
    const navigate = useNavigate();

    // Folder destination — local override so picking a folder here doesn't
    // disturb the background list view. Initialised from the active folder
    // and kept in sync when the dialog is opened for a different folder.
    const [folderPickerOpen, setFolderPickerOpen] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState<number | null>(
      activeFolderId ?? null,
    );
    const [selectedFolderPath, setSelectedFolderPath] = useState<string[]>(
      pathToActiveNode ?? [],
    );

    // Re-sync when the host supplies a new active folder (e.g. dialog reopened
    // from a different node) — but only while the in-dialog picker is closed so
    // an in-progress selection is never clobbered.
    useEffect(() => {
      if (folderPickerOpen) return;
      setSelectedFolderId(activeFolderId ?? null);
      setSelectedFolderPath(pathToActiveNode ?? []);
    }, [activeFolderId, pathToActiveNode, folderPickerOpen]);

    const toggleFolderPicker = useCallback(
      (open: boolean) => {
        setFolderPickerOpen(open);
        onFolderPickerOpenChange?.(open);
      },
      [onFolderPickerOpenChange],
    );

    const currentWorkspaceType =
      useWorkspaceVersions?.data?.workspace?.workspaceType ??
      useWorkspaceVersions?.data?.workspace?.type;

    const isMobileNative = useMemo(() => {
      if (!currentWorkspaceType) return false;
      const str = String(currentWorkspaceType)
        .replace(/[\s_-]/g, "")
        .toLowerCase();
      return (
        currentWorkspaceType === WorkspaceType.AndroidNative ||
        currentWorkspaceType === WorkspaceType.IOSNative ||
        str === "android" ||
        str === "ios" ||
        str === "androidnative" ||
        str === "iosnative"
      );
    }, [currentWorkspaceType]);

    const formSchema = useMemo(
      () => createSchema(isMobileNative),
      [isMobileNative],
    );

    const { control, handleSubmit, setValue, getValues, watch } = useForm({
      resolver: zodResolver(formSchema),
      mode: "onBlur",
      defaultValues: {
        title: editData?.name ?? "",
        websiteUrl: editData?.websiteUrl ?? "",
        description: editData?.description ?? "",
      },
    });
    const watchedFormValues = watch();

    // Snapshot of every editable field's initial value, captured once when the
    // dialog opens — used to detect whether anything has actually changed so the
    // host (EditTestCaseDialog) can keep its "Update" button disabled until then.
    const initialSnapshotRef = useRef({
      testCaseType: editData ? (isAutomationCase(editData) ? "automation" : "manual") : "automation",
      tagIds: (editData?._prefillTags ?? []).map((t: any) => t.id).join(","),
      priorityId: editData?._prefillPriority?.id ?? null,
      typeId: editData?._prefillType?.id ?? null,
      statusId: editData?._prefillStatus?.id ?? null,
      preRequisiteId: editData?._prefillPreRequisite?.id ?? null,
      precondition: stripHtml(editData?.precondition),
      testDataProfileId: editData?._prefillTestData?.id ?? null,
      assigneeIds: (editData?._prefillAssignee ?? [])
        .map((a: any) => a.id)
        .join(","),
      reviewerIds: (editData?._prefillReviewer ?? [])
        .map((r: any) => r.id)
        .join(","),
      requirementId: editData?._prefillRequirement?.id ?? null,
      moduleTypeId: editData?.module_type_id ?? editData?.moduleTypeId ?? null,
      isDataDriven: editData?.isDataDriven ?? false,
      title: editData?.name ?? "",
      websiteUrl: editData?.websiteUrl ?? "",
      description: editData?.description ?? "",
    });

    useEffect(() => {
      if (editData) {
        setModuleTypeId(
          editData?.module_type_id ?? editData?.moduleTypeId ?? null,
        );
      }
    }, [editData]);

    // Auto-fill website URL from the active project when the form URL is still empty (for web workspaces)
    useEffect(() => {
      if (isMobileNative) return;
      const projectWebsiteUrl =
        useWorkspaceVersions?.data?.workspace?.project?.websiteurl;
      if (projectWebsiteUrl && !getValues("websiteUrl")) {
        setValue("websiteUrl", projectWebsiteUrl, { shouldValidate: false });
        handleUrlValidation(projectWebsiteUrl);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      useWorkspaceVersions?.data?.workspace?.project?.websiteurl,
      isMobileNative,
    ]);

    // Recompute whether the form differs from its initial snapshot whenever any
    // editable field changes, and notify the host dialog (edit mode only).
    // NOTE: reads RHF values via getValues() (fresh at effect-run time) rather
    // than the `watchedFormValues` render-closure value, which can lag behind
    // on the very first render before react-hook-form finishes syncing.
    useEffect(() => {
      if (!editData || !onDirtyChange) return;
      const s = initialSnapshotRef.current;
      const formValues = getValues();
      const isDirty =
        testCaseType !== s.testCaseType ||
        (selectedItems ?? []).map((t: any) => t.id).join(",") !== s.tagIds ||
        (priority?.id ?? null) !== s.priorityId ||
        (type?.id ?? null) !== s.typeId ||
        (status?.id ?? null) !== s.statusId ||
        (preRequisite?.id ?? null) !== s.preRequisiteId ||
        precondition !== s.precondition ||
        (testDataProfile?.id ?? null) !== s.testDataProfileId ||
        assignee.map((a: any) => a.id).join(",") !== s.assigneeIds ||
        reviewer.map((r: any) => r.id).join(",") !== s.reviewerIds ||
        (requirement?.id ?? null) !== s.requirementId ||
        (moduleTypeId ?? null) !== s.moduleTypeId ||
        isDataDriven !== s.isDataDriven ||
        (formValues.title ?? "") !== s.title ||
        (formValues.websiteUrl ?? "") !== s.websiteUrl ||
        (formValues.description ?? "") !== s.description;
      onDirtyChange(isDirty);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      editData,
      testCaseType,
      selectedItems,
      priority,
      type,
      status,
      preRequisite,
      precondition,
      testDataProfile,
      assignee,
      reviewer,
      requirement,
      moduleTypeId,
      isDataDriven,
      watchedFormValues.title,
      watchedFormValues.websiteUrl,
      watchedFormValues.description,
    ]);

    const handleTitleCheck = useCallback(
      (title: string) => {
        if (titleCheckTimer.current) clearTimeout(titleCheckTimer.current);

        const trimmed = title?.trim();
        if (!trimmed) {
          setTitleCheckStatus("idle");
          onTitleDuplicateChange?.(false);
          return;
        }

        // In edit mode, if the name hasn't changed from the original, skip the check
        if (editData?.id && trimmed === editData?.name?.trim()) {
          setTitleCheckStatus("available");
          onTitleDuplicateChange?.(false);
          return;
        }

        setTitleCheckStatus("checking");

        const seq = ++titleCheckSeq.current;
        titleCheckTimer.current = setTimeout(async () => {
          try {
            const versionId = userPreferenceData?.data?.versionId;
            if (!versionId) {
              if (seq !== titleCheckSeq.current) return;
              setTitleCheckStatus("idle");
              onTitleDuplicateChange?.(false);
              return;
            }
            const res = await checkTestCaseNameExists(
              trimmed,
              versionId,
              editData?.id ?? undefined,
            );
            // Ignore out-of-order resolutions — only the latest request wins.
            if (seq !== titleCheckSeq.current) return;
            if (res?.data?.exists) {
              setTitleCheckStatus("duplicate");
              onTitleDuplicateChange?.(true);
            } else {
              setTitleCheckStatus("available");
              onTitleDuplicateChange?.(false);
            }
          } catch {
            if (seq !== titleCheckSeq.current) return;
            setTitleCheckStatus("idle");
            onTitleDuplicateChange?.(false);
          }
        }, 500);
      },
      [userPreferenceData, onTitleDuplicateChange, editData],
    );

    const handleUrlValidation = useCallback((url: string) => {
      if (urlValidationTimer.current) clearTimeout(urlValidationTimer.current);

      const trimmedUrl = url?.trim();
      if (!trimmedUrl) {
        setUrlValidationStatus("idle");
        return;
      }

      // Basic URL format check
      const urlPattern =
        /(?:^|\s)(((https)?:\/\/)?(?:localhost|[\w-]+(?:\.[\w-]+)+)(:\d+)?(\/\S*)?)/;
      if (!urlPattern.test(trimmedUrl)) {
        setUrlValidationStatus("idle");
        return;
      }

      setUrlValidationStatus("validating");

      urlValidationTimer.current = setTimeout(async () => {
        try {
          const res = await validateURLAccess(trimmedUrl);
          // Server returns empty array if URL is accessible
          if (res?.data?.length === 0) {
            setUrlValidationStatus("accessible");
          } else {
            setUrlValidationStatus("not-accessible");
          }
        } catch {
          setUrlValidationStatus("not-accessible");
        }
      }, 500);
    }, []);

    const onSubmit =
      (actionType: string) =>
      (formData: z.infer<ReturnType<typeof createSchema>>) => {
        // Double-submit / re-entry guard: ignore the call if a create or update
        // is already in flight. This is independent of the parent's
        // isSavingTestCase flag (which is only set asynchronously via
        // onSavingChange a render later), so it closes the rapid double-click gap.
        if (saveTestCaseMutation.isPending || updateTestCaseMutation.isPending)
          return;
        if (titleCheckStatus === "duplicate") return;
        const totalItems = testDataProfile?.data?.length ?? 0;

        let startIndex = 0;
        let endIndex = totalItems > 0 ? totalItems - 1 : null;

        // If user selects a specific start dataset (not "start")
        if (startDataset && startDataset.id !== "start") {
          const foundIndex = testDataProfile?.data?.findIndex(
            (item) => item.id === startDataset?.id,
          );
          startIndex = foundIndex !== -1 ? foundIndex : 0;
        }

        // If user selects a specific end dataset (not "end")
        if (endDataset && endDataset.id !== "end") {
          const foundIndex = testDataProfile?.data?.findIndex(
            (item) => item.id === endDataset?.id,
          );
          endIndex = foundIndex !== -1 ? foundIndex : totalItems - 1;
        }
        const payload = {
          assigneeid: assignee?.[0]?.id ?? null,
          deleted: false,
          description: formData.description || "",
          // isAutomatedScript: testCaseType === "automation",
          scriptType: testCaseType,
          isDataDriven: isDataDriven,
          isStepGroup: false,
          name: formData.title,
          preRequisite: preRequisite?.id ?? null,
          precondition: precondition || null,
          priority: priority?.id ?? null,
          requirementId: requirement?.id ?? null,
          reviewerid: reviewer?.[0]?.id ?? null,
          sendMailNotification: false,
          status: status?.name || "DRAFT",
          tags: selectedItems.map((item) => item.name),
          testData: testDataProfile,
          testDataStartIndex: startIndex,
          testDataEndIndex: endIndex,
          testDataId: testDataProfile?.id ?? null,
          type: type?.id ?? null,
          moduleTypeId: moduleTypeId ?? null,
          moduleTypeName: moduleTypeName ?? null,
          projectId: projectId,
          websiteUrl: formData.websiteUrl?.trim()
            ? formData.websiteUrl.trim()
            : null,
          workspaceVersionId: userPreferenceData?.data?.versionId,
          testCaseFolderId: selectedFolderId,
        };

        onSavingChange?.(true);
        if (editData?.id) {
          // Edit mode — update the existing test case
          updateTestCaseMutation.mutate(payload, {
            onSuccess: (data) => {
              onSavingChange?.(false);
              onCloseDialog(data);
            },
            onError: (error: any) => {
              onSavingChange?.(false);
              if ((error as any)?.response?.status === 402) return;
              showToast({
                variant: "error",
                title:
                  (error as any)?.response?.data?.error ??
                  "Failed to update test case",
              });
            },
          });
        } else {
          saveTestCaseMutation.mutate(payload, {
            onSuccess: (data) => {
              onSavingChange?.(false);
              const created = data?.data;
              const createdId = created?.id;
              const createdScriptType = (
                created?.scriptType ?? testCaseType ?? "automation"
              ).toLowerCase();
              const recordingUrlRaw =
                created?.websiteUrl ??
                formData.websiteUrl?.trim() ??
                "";
              const recordingUrl =
                recordingUrlRaw && !/^https?:\/\//i.test(recordingUrlRaw)
                  ? `https://${recordingUrlRaw.replace(/^\/\//, "")}`
                  : recordingUrlRaw;
              if (createdId) {
                tabStore.openTab({
                  id: Number(createdId),
                  name: created?.name || formData.title || `TC-${createdId}`,
                  scriptType: createdScriptType,
                  folderId:
                    created?.testCaseFolderId ??
                    selectedFolderId ??
                    undefined,
                  testCaseRaw: created,
                });
              }
              onCloseDialog(data);
              if (actionType === "RECORD") {
                const testCaseId = createdId;
                if (testCaseId) {
                  // Start recorder directly for the NEWLY created case. This
                  // avoids stale `isStepRecord` URL coupling to an older active tab.
                  window.postMessage(
                    {
                      from: AUTOPILOT_SERV,
                      type: MESSAGE_START_RECORDING,
                      content: "Start Recording",
                      baseUrl: recordingUrl,
                      test_case_id: testCaseId,
                      version_id:
                        created?.workspaceVersionId ??
                        userPreferenceData?.data?.versionId,
                      applicationId: userPreferenceData?.data?.workspaceId,
                      source: "OS",
                      applicationType:
                        useWorkspaceVersions?.data?.workspace?.workspaceType,
                      serverUrl: EnvConfig.serverURL,
                    },
                    "*",
                  );
                  const nextSearch = new URLSearchParams();
                  if (selectedFolderId) {
                    nextSearch.set("folder", String(selectedFolderId));
                  }
                  navigate(
                    `/testcases/${testCaseId}${nextSearch.toString() ? `?${nextSearch.toString()}` : ""}`,
                  );
                } else {
                  console.error("testcaseid not found in response");
                }
              }
              // "Write Manual Steps" (actionType === "SAVE") needs no extra
              // navigation here — onCloseDialog(data) above already routed to
              // the clean `/testcases/{id}?folder={folderId}` URL. Re-navigating
              // to the legacy `/testpage/{scriptType}` path would clobber that
              // and drop the folder context.
            },
            onError: (error: any) => {
              onSavingChange?.(false);
              // 402 is handled globally by PaywallModal via the axios interceptor
              if ((error as any)?.response?.status === 402) return;
              showToast({
                variant: "error",
                title:
                  (error as any)?.response?.data?.error ??
                  "Failed to create test case",
              });
            },
          });
        }
      };

    useImperativeHandle(ref, () => ({
      submit: (actionType: string) => {
        handleSubmit(onSubmit(actionType))();
      },
    }));

    return (
      <div
        className="flex w-full h-full overflow-hidden"
        data-tour="test-case-create-dialog"
      >
        {!editData && (
          <div
            className="h-full shrink-0 overflow-hidden transition-[width] duration-200 ease"
            style={{ width: folderPickerOpen ? 260 : 0 }}
          >
            <FolderPickerPanel
              versionId={userPreferenceData?.data?.versionId}
              folderType="TESTCASE"
              selectedFolderId={selectedFolderId}
              onSelect={(folderId, pathTitles) => {
                setSelectedFolderId(folderId);
                setSelectedFolderPath(pathTitles);
              }}
              onClose={() => toggleFolderPicker(false)}
            />
          </div>
        )}
        <ScrollArea className="flex flex-col basis-[70%] min-w-0 ">
          <Box className="px-6 py-4">
            {/* <Flex direction="column" className="space-y-4 mb-4" gap={"2"}>
              <Flex
                direction="column"
                className="w-full justify-between items-start bg-primary-1 px-4 pt-2 pb-2 rounded-sm"
              >
                <span className="font-semibold text-xs text-text-primary">
                  {useWorkspaceVersions?.data?.workspace?.project?.name ||
                    "Sample Project"}
                </span>
                <span className="text-extrasmall text-text-secondary">
                  {useWorkspaceVersions?.data?.workspace?.name ||
                    "Sample Workspace"}
                </span>
              </Flex>
            </Flex> */}

            <div className="flex flex-col flex-1 space-y-4 overflow-hidden">
              <div>
                <TextLabel>Test Case Type</TextLabel>
                <div className="mt-1.5">
                  <div
                    className={editData ? "opacity-60 pointer-events-none" : ""}
                  >
                    <PillTabs
                      tabs={[
                        { label: "Automation", value: "automation", icon: Cpu },
                        { label: "Manual", value: "manual", icon: Hand },
                      ]}
                      activeTab={testCaseType}
                      onTabChange={(val) => {
                        if (editData) return;
                        setTestCaseType(val as "automation" | "manual");
                        onChangeTestCaseType(val as "automation" | "manual");
                      }}
                    />
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 px-1"
              >
                <div className="flex gap-4">
                  <Box className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {isFieldLocked("title") && (
                        <Tooltip
                          content="Locked — unlock test case to edit title"
                          side="top"
                        >
                          <Lock size={12} className="text-amber-500" />
                        </Tooltip>
                      )}
                      <TextLabel require>Title</TextLabel>
                    </div>
                    <TextInputControl
                      name="title"
                      control={control}
                      type="text"
                      placeholder="e.g. Checkout flow — guest user"
                      className="w-full"
                      disabled={isFieldLocked("title")}
                      onChange={() => {
                        setTitleCheckStatus("idle");
                        onTitleDuplicateChange?.(false);
                      }}
                      onBlur={(e) => handleTitleCheck(e.target.value)}
                    />
                    {titleCheckStatus === "checking" && (
                      <Flex py={"1"} className="flex items-center gap-1">
                        <Loader2
                          size={12}
                          className="animate-spin text-muted-foreground"
                        />
                        <Text className="text-extrasmall text-muted-foreground">
                          Checking title...
                        </Text>
                      </Flex>
                    )}
                    {titleCheckStatus === "available" && (
                      <Flex py={"1"} className="flex items-center gap-1">
                        <CheckCircle2 size={12} className="text-green-600" />
                        <Text className="text-extrasmall text-green-600">
                          Title is available
                        </Text>
                      </Flex>
                    )}
                    {titleCheckStatus === "duplicate" && (
                      <Flex py={"1"} className="flex items-center gap-1">
                        <XCircle size={12} className="text-red-500" />
                        <Text className="text-extrasmall text-red-500">
                          A test case with this title already exists
                        </Text>
                      </Flex>
                    )}

                    <div className="flex items-center gap-1.5 pt-1.5 w-full">
                      <FolderOpen
                        size={15}
                        className="text-text-secondary-inverse shrink-0"
                      />

                      {selectedFolderPath && selectedFolderPath.length > 0 ? (
                        <div className="overflow-hidden flex-1 min-w-0">
                          <Tooltip
                            content={selectedFolderPath.join(" / ")}
                            side="top"
                          >
                            <div className="text-text-secondary-inverse text-extrasmall truncate block w-full">
                              {selectedFolderPath.join(" / ")}
                            </div>
                          </Tooltip>
                        </div>
                      ) : (
                        <Text className="text-extrasmall text-muted-foreground flex-1">
                          No folder selected
                        </Text>
                      )}

                      {!editData && !folderPickerOpen && (
                        <button
                          type="button"
                          onClick={() => toggleFolderPicker(true)}
                          className="ml-2 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-muted-foreground bg-gray-100 hover:text-foreground hover:bg-gray-200 transition-colors shrink-0 whitespace-nowrap"
                        >
                          <Pencil className="size-3" /> Change folder
                        </button>
                      )}
                    </div>
                  </Box>
                </div>
                <Box className="flex-1">
                  <TextLabel require={!isMobileNative}>Website URL</TextLabel>
                  <Controller
                    name="websiteUrl"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <div className="pt-1">
                        <InputGroup hasValue={!!value}>
                          <InputGroupAddon>https://</InputGroupAddon>
                          <InputGroupInput
                            ref={ref}
                            placeholder="example.com"
                            value={(value || "").replace(/^https?:\/\//, "")}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const full = raw.trim()
                                ? raw.startsWith("http")
                                  ? raw
                                  : `https://${raw}`
                                : "";
                              onChange(full);
                              setUrlValidationStatus("idle");
                            }}
                            onBlur={(e) => {
                              onBlur();
                              const raw = e.target.value;
                              const full = raw.trim()
                                ? raw.startsWith("http")
                                  ? raw
                                  : `https://${raw}`
                                : "";
                              if (full) {
                                handleUrlValidation(full);
                              } else {
                                setUrlValidationStatus("idle");
                              }
                            }}
                          />
                          <span className="flex shrink-0 items-center pr-3">
                            <Tooltip
                              content={
                                isMobileNative
                                  ? "Optional: website or deep link URL for this mobile test case"
                                  : "This URL will be used as the base URL for automated test execution"
                              }
                              side="top"
                              sideOffset={5}
                            >
                              <Info
                                size={16}
                                className="text-muted-foreground hover:text-primary cursor-pointer transition-colors"
                              />
                            </Tooltip>
                          </span>
                        </InputGroup>
                      </div>
                    )}
                  />
                  {!isMobileNative && urlValidationStatus === "idle" && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {!getValues("websiteUrl") &&
                      !useWorkspaceVersions?.data?.workspace?.project
                        ?.websiteurl
                        ? "No project URL configured — set it in Project Settings to auto-fill."
                        : "For example: https://example.com"}
                    </p>
                  )}
                  {urlValidationStatus === "validating" && (
                    <Flex py={"1"} className="flex items-center gap-1">
                      <Loader2
                        size={12}
                        className="animate-spin text-muted-foreground"
                      />
                      <Text className="text-extrasmall text-muted-foreground">
                        Validating URL accessibility...
                      </Text>
                    </Flex>
                  )}
                  {urlValidationStatus === "accessible" && (
                    <Flex py={"1"} className="flex items-center gap-1">
                      <CheckCircle2 size={12} className="text-green-600" />
                      <Text className="text-extrasmall text-green-600">
                        URL is accessible
                      </Text>
                    </Flex>
                  )}
                  {urlValidationStatus === "not-accessible" && (
                    <Flex py={"1"} className="flex items-center gap-1">
                      <XCircle size={12} className="text-red-500" />
                      <Text className="text-extrasmall text-red-500">
                        This URL is not accessible. Please enter a valid URL
                      </Text>
                    </Flex>
                  )}
                </Box>
                {testCaseType === "manual" && (
                  <Box className="flex-1">
                    <div className="flex items-center gap-1.5">
                      {isFieldLocked("precondition") && (
                        <Tooltip
                          content="Locked — unlock test case to edit precondition"
                          side="top"
                        >
                          <Lock size={12} className="text-amber-500" />
                        </Tooltip>
                      )}
                      <TextLabel>Precondition</TextLabel>
                    </div>
                    <textarea
                      value={precondition}
                      onChange={(e) =>
                        !isFieldLocked("precondition") &&
                        setPrecondition(e.target.value)
                      }
                      placeholder="Enter any preconditions for this test case"
                      rows={4}
                      disabled={isFieldLocked("precondition")}
                      className={`w-full mt-1.5 rounded-md border border-border bg-white px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-ring resize-y min-h-[96px] transition-colors ${isFieldLocked("precondition") ? "cursor-not-allowed bg-muted/50 resize-none" : ""}`}
                    />
                  </Box>
                )}
                <Box className="flex-1">
                  <div className="flex items-center gap-1.5">
                    {isFieldLocked("description") && (
                      <Tooltip
                        content="Locked — unlock test case to edit description"
                        side="top"
                      >
                        <Lock size={12} className="text-amber-500" />
                      </Tooltip>
                    )}
                    <TextLabel>Description</TextLabel>
                  </div>
                  <TextAreaControl
                    name="description"
                    control={control}
                    placeholder="What does this test verify? AI uses this to suggest steps."
                    rows={4}
                    className="w-full"
                    disabled={isFieldLocked("description")}
                  />
                </Box>
              </form>
            </div>
          </Box>
        </ScrollArea>

        <ScrollArea className="basis-[30%] min-w-0 border-l border-gray-100 overflow-auto">
          <Box className="px-6 py-4 space-y-4">
            <Box>
              <div className="flex items-center gap-1.5">
                {isFieldLocked("priority") && (
                  <Tooltip
                    content="Locked — unlock test case to edit priority"
                    side="top"
                  >
                    <Lock size={12} className="text-amber-500" />
                  </Tooltip>
                )}
                <TextLabel>Priority</TextLabel>
              </div>
              <SelectField
                options={priorityList}
                value={priority?.id}
                onValueChange={setPriority}
                placeholder="Priority"
                className="mt-1"
                disabled={isFieldLocked("priority")}
                renderOption={(option: any) => (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: option?.color || "#9ca3af" }}
                    />
                    {option?.displayName ||
                      (option?.name || "")
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </span>
                )}
                renderValue={(option: any) => (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: option?.color || "#9ca3af" }}
                    />
                    {option?.displayName ||
                      (option?.name || "")
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </span>
                )}
              />
            </Box>

            <Box>
              <TextLabel>Type of Test Case</TextLabel>
              <SelectField
                options={typeList}
                value={type?.id}
                onValueChange={setType}
                placeholder="Type"
                className="mt-1"
              />
            </Box>

            <Box>
              <TextLabel>Module Type</TextLabel>
              <ModuleTypeSelect
                projectId={projectId}
                value={moduleTypeId}
                onChange={(id, name) => {
                  setModuleTypeId(id);
                  setModuleTypeName(name);
                }}
                className="mt-1"
              />
            </Box>

            <Box>
              <TextLabel>Status</TextLabel>
              <SelectField
                options={statusList}
                value={status?.id}
                onValueChange={setStatus}
                placeholder="Status"
                className="mt-1"
                renderOption={(option: any) => (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: option?.color || "#6b7280" }}
                    />
                    {option?.displayName ||
                      (option?.name || "")
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </span>
                )}
                renderValue={(option: any) => (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: option?.color || "#6b7280" }}
                    />
                    {option?.displayName ||
                      (option?.name || "")
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </span>
                )}
              />
            </Box>

            {testCaseType === "automation" && (
              <Box>
                <div className="flex items-center gap-1.5">
                  {isFieldLocked("prerequisite") && (
                    <Tooltip
                      content="Locked — unlock test case to edit prerequisite"
                      side="top"
                    >
                      <Lock size={12} className="text-amber-500" />
                    </Tooltip>
                  )}
                  <TextLabel>Prerequisite</TextLabel>
                </div>
                <SelectField
                  options={prerequisiteList.filter(
                    (tc: any) => tc.scriptType?.toLowerCase() === testCaseType,
                  )}
                  value={preRequisite?.id}
                  onValueChange={setPreRequisite}
                  placeholder="Prerequisite"
                  searchable
                  clearable
                  className="mt-1"
                  disabled={isFieldLocked("prerequisite")}
                  renderOption={(option: any) =>
                    option?.name || option?.title || `Test Case #${option?.id}`
                  }
                  renderValue={(option: any) =>
                    option?.name || option?.title || `Test Case #${option?.id}`
                  }
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Another test case that must run first to set up state for this
                  one.
                </p>
              </Box>
            )}

            {testCaseType === "automation" && (
              <>
                <Box>
                  <TextLabel>Test Data Profile</TextLabel>
                  <SelectField
                    options={testDataList}
                    value={testDataProfile?.id}
                    onValueChange={(value) => {
                      setTestDataProfile(value);
                      setSwitchEnabled(!!value);
                      if (!value) setIsDataDriven(false);
                    }}
                    labelKey={"testDataName"}
                    valueKey={"id"}
                    placeholder="Test Data Profile"
                    searchable
                    clearable
                    className="mt-1"
                    renderOption={(option: any) =>
                      option?.testDataName ||
                      option?.name ||
                      `Profile #${option?.id}`
                    }
                    renderValue={(option: any) =>
                      option?.testDataName ||
                      option?.name ||
                      `Profile #${option?.id}`
                    }
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Drive this test with rows from a data set. Enable “Data
                    Driven” to repeat the test once per row.
                  </p>
                </Box>
              </>
            )}
            {testCaseType === "automation" && (
              <div className="flex items-center gap-2">
                <>
                  <TextLabel>Data Driven</TextLabel>
                  <Switch
                    disabled={!switchEnabled}
                    checked={isDataDriven}
                    onCheckedChange={(checked) => setIsDataDriven(checked)}
                  />
                </>
              </div>
            )}
            {isDataDriven &&
              (extractList(testDataProfile?.data).length > 0 ||
                extractList(testDataProfile?.testDataValues).length > 0) && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Dataset */}
                  <Box>
                    <TextLabel>Start Dataset</TextLabel>
                    <SelectField
                      options={[
                        { id: "start", displayName: "Start (First Dataset)" },
                        ...extractList(
                          testDataProfile?.data ??
                            testDataProfile?.testDataValues,
                        ).map((item: any, index: number) => ({
                          ...item,
                          displayName: `#${item.position ?? index + 1} - ${item.name}`,
                        })),
                      ]}
                      value={startDataset?.id}
                      labelKey={"displayName"}
                      valueKey={"id"}
                      placeholder="Select start dataset"
                      onValueChange={(value) => setStartDataset(value)}
                    />
                  </Box>

                  {/* End Dataset */}
                  <Box>
                    <TextLabel>End Dataset</TextLabel>
                    <SelectField
                      options={[
                        { id: "end", displayName: "End (Last Dataset)" },
                        ...extractList(
                          testDataProfile?.data ??
                            testDataProfile?.testDataValues,
                        ).map((item: any, index: number) => ({
                          ...item,
                          displayName: `#${item.position ?? index + 1} - ${item.name}`,
                        })),
                      ]}
                      value={endDataset?.id}
                      labelKey={"displayName"}
                      valueKey={"id"}
                      placeholder="Select end dataset"
                      onValueChange={(value) => setEndDataset(value)}
                    />
                  </Box>
                </div>
              )}

            <Box>
              <TextLabel>Tags</TextLabel>
              <MultiSearchableSelect
                options={tagList}
                selected={selectedItems}
                onChange={setSelectedItems}
                placeholder="Tags"
                className="mt-1"
              />
            </Box>
            <Box>
              <TextLabel>Requirements</TextLabel>
              <SelectField
                options={requirementList}
                value={requirement?.id}
                onValueChange={setRequirement}
                placeholder="Requirements"
                searchable
                clearable
                className="mt-1"
                renderOption={(option: any) =>
                  option?.name ||
                  option?.title ||
                  option?.requirementName ||
                  `Requirement #${option?.id}`
                }
                renderValue={(option: any) =>
                  option?.name ||
                  option?.title ||
                  option?.requirementName ||
                  `Requirement #${option?.id}`
                }
              />
            </Box>
            <Box>
              <TextLabel>Assignee</TextLabel>
              <MultiUserSelect
                users={teamMemberList}
                value={assignee}
                onValueChange={setAssignee}
                placeholder="Select assignees"
                className="mt-1"
                chipStyle
                densityMode="form"
              />
            </Box>
            <Box>
              <TextLabel>Reviewer</TextLabel>
              <MultiUserSelect
                users={reviewerCandidates}
                value={reviewer}
                onValueChange={setReviewer}
                placeholder="Select reviewers"
                className="mt-1"
                chipStyle
                densityMode="form"
              />
            </Box>
          </Box>
          <div className="h-20" />
        </ScrollArea>
      </div>
    );
  },
);

export default CreateTest;
